import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Newspaper, Plus, Trash2, Calendar, User, Link } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Gazzetta = () => {
  const { canPublishGazzetta } = useAuth();
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    titolo: '',
    contenuto: '',
    categoria: 'Annuncio',
    link: ''
  });

  const canWrite = canPublishGazzetta();

  useEffect(() => {
    fetchNews();
  }, []);

  const fetchNews = async () => {
    try {
      const response = await axios.get(`${API_URL}/gazzetta`);
      setNews(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento delle notizie');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/gazzetta`, {
        ...formData,
        link: formData.link || null
      });
      toast.success('Notizia pubblicata con successo');
      setIsDialogOpen(false);
      setFormData({ titolo: '', contenuto: '', categoria: 'Annuncio', link: '' });
      fetchNews();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella pubblicazione');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Sei sicuro di voler eliminare questa notizia?')) return;
    try {
      await axios.delete(`${API_URL}/gazzetta/${id}`);
      toast.success('Notizia eliminata');
      fetchNews();
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  const categories = ['Annuncio', 'Comunicato Ufficiale', 'Evento', 'Avviso', 'Ordinanza'];

  return (
    <div data-testid="gazzetta-page" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="page-header flex items-center gap-3">
            <Newspaper className="w-8 h-8" />
            Gazzetta Ufficiale
          </h1>
          <p className="text-slate-600 mt-1">News e annunci pubblici di Nova Roleplay</p>
        </div>
        {canWrite && (
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="create-news-btn" className="bg-slate-900 hover:bg-slate-800 rounded-sm">
                <Plus className="w-4 h-4 mr-2" />
                Nuova Notizia
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-serif text-xl">Pubblica Notizia</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="titolo">Titolo</Label>
                  <Input
                    id="titolo"
                    data-testid="news-title-input"
                    value={formData.titolo}
                    onChange={(e) => setFormData({ ...formData, titolo: e.target.value })}
                    placeholder="Titolo della notizia"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="categoria">Categoria</Label>
                  <Select
                    value={formData.categoria}
                    onValueChange={(value) => setFormData({ ...formData, categoria: value })}
                  >
                    <SelectTrigger data-testid="news-category-select">
                      <SelectValue placeholder="Seleziona categoria" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((cat) => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="contenuto">Contenuto</Label>
                  <Textarea
                    id="contenuto"
                    data-testid="news-content-input"
                    value={formData.contenuto}
                    onChange={(e) => setFormData({ ...formData, contenuto: e.target.value })}
                    placeholder="Scrivi il contenuto della notizia..."
                    rows={6}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="link">Link (opzionale)</Label>
                  <Input
                    id="link"
                    data-testid="news-link-input"
                    value={formData.link}
                    onChange={(e) => setFormData({ ...formData, link: e.target.value })}
                    placeholder="https://..."
                    type="url"
                  />
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                    Annulla
                  </Button>
                  <Button type="submit" data-testid="submit-news-btn" className="bg-slate-900 hover:bg-slate-800">
                    Pubblica
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* News Grid */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
        </div>
      ) : news.length === 0 ? (
        <Card className="border-dashed border-2">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Newspaper className="w-12 h-12 text-slate-300 mb-4" />
            <p className="text-slate-500">Nessuna notizia pubblicata</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {news.map((item) => (
            <Card key={item.id} data-testid={`news-card-${item.id}`} className="border border-slate-200 hover:shadow-md transition-shadow">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div>
                    <span className="inline-block px-2 py-0.5 text-xs font-medium bg-amber-100 text-amber-800 rounded mb-2">
                      {item.categoria}
                    </span>
                    <CardTitle className="font-serif text-xl">{item.titolo}</CardTitle>
                  </div>
                  {canWrite && (
                    <Button
                      variant="ghost"
                      size="sm"
                      data-testid={`delete-news-${item.id}`}
                      onClick={() => handleDelete(item.id)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-slate-600 whitespace-pre-wrap">{item.contenuto}</p>
                {item.link && (
                  <a 
                    href={item.link} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 mt-3 text-blue-600 hover:text-blue-800 text-sm"
                  >
                    <Link className="w-4 h-4" />
                    Apri link allegato
                  </a>
                )}
                <div className="flex items-center gap-4 mt-4 pt-4 border-t border-slate-100 text-sm text-slate-500">
                  <span className="flex items-center gap-1">
                    <User className="w-4 h-4" />
                    {item.autore_nome}
                  </span>
                  <span className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    {new Date(item.created_at).toLocaleDateString('it-IT', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric'
                    })}
                  </span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default Gazzetta;
